/**
 * KAN-755: скидки и группы товаров: типы скидок (реестр формул), список
 * скидок с фильтрами, карточка скидки, группы и правки. После любой правки
 * обновляются скидки, группы и каталог (блок "Скидки на товар" в карточке).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { discountsApi, productGroupsApi } from '@/services/api/discounts';
import { CATALOG_QUERY_KEY, DISCOUNTS_QUERY_KEY } from '@/services/api/queryKeys';
import type { DiscountBody, DiscountsListParams, ProductGroupBody } from '@/types/discount';

export const discountTypesKey = [...DISCOUNTS_QUERY_KEY, 'types'] as const;
export const discountsListKey = (params: DiscountsListParams) => [...DISCOUNTS_QUERY_KEY, 'list', params] as const;
export const discountDetailKey = (id: string | null | undefined) => [...DISCOUNTS_QUERY_KEY, 'detail', id] as const;
export const productGroupsKey = [...DISCOUNTS_QUERY_KEY, 'groups'] as const;
export const productGroupKey = (id: string | null | undefined) => [...DISCOUNTS_QUERY_KEY, 'group', id] as const;

export function useDiscountTypes() {
  return useQuery({
    queryKey: discountTypesKey,
    queryFn: discountsApi.types,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useDiscounts(params: DiscountsListParams) {
  return useQuery({
    queryKey: discountsListKey(params),
    queryFn: () => discountsApi.list(params),
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** id null: запрос не уходит (форма новой скидки). */
export function useDiscount(id: string | null | undefined) {
  return useQuery({
    queryKey: discountDetailKey(id),
    queryFn: () => discountsApi.get(id as string),
    enabled: !!id,
    retry: false,
  });
}

export function useProductGroups(enabled = true) {
  return useQuery({
    queryKey: productGroupsKey,
    queryFn: () => productGroupsApi.list(),
    retry: false,
    enabled,
  });
}

export function useProductGroup(id: string | null | undefined) {
  return useQuery({
    queryKey: productGroupKey(id),
    queryFn: () => productGroupsApi.get(id as string),
    enabled: !!id,
    retry: false,
  });
}

function useInvalidateDiscounts() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: DISCOUNTS_QUERY_KEY });
    void client.invalidateQueries({ queryKey: CATALOG_QUERY_KEY });
  };
}

export function useDiscountMutations() {
  const onSuccess = useInvalidateDiscounts();
  return {
    create: useMutation({
      mutationFn: (body: DiscountBody) => discountsApi.create(body),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (args: { id: string; body: Partial<DiscountBody> }) => discountsApi.update(args.id, args.body),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => discountsApi.remove(id),
      onSuccess,
    }),
  };
}

export function useProductGroupMutations() {
  const onSuccess = useInvalidateDiscounts();
  return {
    create: useMutation({
      mutationFn: (body: ProductGroupBody) => productGroupsApi.create(body),
      onSuccess,
    }),
    update: useMutation({
      mutationFn: (args: { id: string; body: Partial<ProductGroupBody> }) =>
        productGroupsApi.update(args.id, args.body),
      onSuccess,
    }),
    remove: useMutation({
      mutationFn: (id: string) => productGroupsApi.remove(id),
      onSuccess,
    }),
  };
}
